import getConfig from 'next/config';
import React, { createContext, useReducer, ReactNode } from 'react';

import NextApiService from 'services/api';
import { ICustomerActivity } from 'services/customerActivitiesService';
import { ACTIVITY_TYPE } from 'utils/constants';

import reducer, {
  AuthState,
  AuthSharedState,
  AuthTypes,
  initialState,
} from './state/AuthState';

const { publicRuntimeConfig } = getConfig();

interface IActions {
  logout: (logoutAccount?: boolean) => void;
  setCustomerActivity: (
    customerId: number | undefined,
    activityType: string,
    accessAt: Date,
    reportId?: number,
    applicationId?: number,
    claimFormId?: number,
  ) => void;
}

export const AuthContext = createContext<{
  state: AuthState;
  actions: IActions;
}>({
  state: initialState,
  actions: { logout: () => Promise.reject(), setCustomerActivity: () => {} },
});

interface IProps extends AuthSharedState {
  children: ReactNode;
}

const api = NextApiService();

const AuthContextProvider: React.FC<IProps> = ({
  user,
  customer,
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    user,
    customer,
  });

  /**
   * ACTIONS
   */
  const setCustomerActivity = async (
    customerId: number | undefined,
    activityType: string,
    accessAt: Date,
    reportId?: number,
    applicationId?: number,
    claimFormId?: number,
  ) => {
    if (!customerId) return;
    const data = {
      customer_id: customerId,
      activity_type: activityType,
      access_dt: accessAt,
      report_id: reportId,
      application_id: applicationId,
      claim_form_id: claimFormId,
    };
    await api.post<ICustomerActivity>('/customer-activities', data);
  };

  const logout = async (logoutAccount?: boolean) => {
    try {
      await setCustomerActivity(
        state.customer.id,
        ACTIVITY_TYPE.LOGOUT_LAUNCHPAD,
        new Date(),
      );
      await api.delete(`/auth/login`, {});
      dispatch({ type: AuthTypes.SIGNOUT_SUCCESS });
      window.location.href = logoutAccount
        ? `https://login.microsoftonline.com/logout.srf`
        : publicRuntimeConfig.logoutURL || '/404';
    } catch (err) {
      console.error(err);
      // dispatch({ type: AuthTypes.SIGNOUT_FAIL });
    }
  };

  const actions: IActions = { logout, setCustomerActivity };

  return (
    <AuthContext.Provider value={{ state, actions }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => React.useContext(AuthContext);

export { useAuth };
export default AuthContextProvider;
